import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowRight, Check, Heart } from "lucide-react";
import { Button } from "../components/ui/button";

import { API } from "../config/api";

// Helper to normalize image URLs
const getImageUrl = (url) => {
  if (!url) return "";
  if (url.startsWith("http://") || url.startsWith("https://")) {
    if (url.includes(".preview.emergentagent.com/api/uploads/")) {
      const filename = url.split("/api/uploads/").pop();
      return `/api/uploads/${filename}`;
    }
    return url;
  }
  if (url.startsWith("/")) return url;
  return "/" + url;
};

export default function CharityPage() {
  const { charitySlug } = useParams();
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);

  // Determine the slug - either from params or from path for legacy routes
  const getSlugFromPath = () => {
    const path = window.location.pathname;
    if (path.includes('charity-food-drive')) return 'food-drive';
    if (path.includes('charity-blood-donation')) return 'blood-donation';
    return charitySlug;
  };

  const slug = getSlugFromPath();
  const apiSlug = `charity-${slug}`;

  useEffect(() => {
    if (!slug) {
      setLoading(false);
      return;
    }
    fetch(`${API}/site-pages/${apiSlug}`)
      .then(res => res.json())
      .then(data => {
        if (!data.not_found) setPageData(data);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [apiSlug, slug]);

  // Helper to get section by type
  const getSection = (type) => pageData?.sections?.find(s => s.type === type);

  const heroSection = getSection("hero") || {
    heading: "Community Initiative",
    subheading: "CHARITY",
    body: "Making a difference together",
    image_url: "https://images.unsplash.com/photo-1488521787991-ed7bbaae773c?w=1920"
  };

  const descSection = getSection("description") || { body: "Content coming soon..." };
  const howToHelpSection = getSection("how_to_help") || { items: [] };
  const acceptedItemsSection = getSection("accepted_items") || { items: [] };
  const eligibilitySection = getSection("eligibility") || { items: [] };

  if (loading) {
    return (
      <div className="pt-20 min-h-screen flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-kerala-green border-t-transparent rounded-full"></div>
      </div>
    );
  }

  if (!pageData) {
    return (
      <div className="pt-20 min-h-screen flex flex-col items-center justify-center bg-kerala-cream">
        <h1 className="font-heading text-3xl text-kerala-green mb-4">Page Not Found</h1>
        <p className="text-gray-600 mb-8">The charity initiative you're looking for doesn't exist yet.</p>
        <Link to="/">
          <Button className="btn-primary">Go Home</Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="pt-20 bg-kerala-cream" data-testid={`charity-${slug}-page`}>
      {/* Hero */}
      <section className="relative py-32 bg-kerala-green overflow-hidden">
        <div 
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage: `url(${getImageUrl(heroSection.image_url)})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        />
        <div className="section-container relative z-10 text-center">
          <p className="text-kerala-gold text-sm tracking-widest uppercase mb-4">{heroSection.subheading}</p>
          <h1 className="font-heading text-5xl md:text-6xl text-white mb-6">
            {heroSection.heading}
          </h1>
          <p className="text-white/80 text-xl max-w-2xl mx-auto">
            {heroSection.body}
          </p>
        </div>
      </section>

      {/* Description */}
      <section className="section-padding">
        <div className="section-container max-w-4xl">
          <div className="text-center">
            <p className="text-lg text-gray-600 leading-relaxed">
              {descSection.body}
            </p>
          </div>
        </div>
      </section>

      {/* How to Help */}
      {howToHelpSection.items?.length > 0 && (
        <section className="section-padding bg-kerala-cream-alt">
          <div className="section-container max-w-4xl">
            <h2 className="font-heading text-3xl text-kerala-green text-center mb-10">
              {howToHelpSection.heading || "How You Can Help"}
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {howToHelpSection.items.map((item, idx) => (
                <div key={idx} className="flex items-start gap-3 bg-white p-4 rounded-lg shadow-sm">
                  <div className="w-8 h-8 bg-kerala-green/10 rounded-full flex items-center justify-center shrink-0">
                    <Heart className="w-4 h-4 text-kerala-green" />
                  </div>
                  <span className="text-gray-700">{item}</span>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Accepted Items / Eligibility */}
      {(acceptedItemsSection.items?.length > 0 || eligibilitySection.items?.length > 0) && (
        <section className="section-padding">
          <div className="section-container max-w-4xl">
            <h2 className="font-heading text-3xl text-kerala-green text-center mb-10">
              {acceptedItemsSection.heading || eligibilitySection.heading || "Details"}
            </h2>
            
            <div className="bg-white rounded-lg p-8 shadow-sm border border-kerala-gold/10">
              <ul className="space-y-3">
                {(acceptedItemsSection.items || eligibilitySection.items || []).map((item, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-kerala-green shrink-0 mt-0.5" />
                    <span className="text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="py-16 bg-kerala-terracotta">
        <div className="section-container text-center">
          <h2 className="font-heading text-3xl text-white mb-4">
            Want to Support Our Community?
          </h2>
          <p className="text-white/90 mb-8 max-w-xl mx-auto">
            Join us in making a positive impact in our community.
          </p>
          <Link to="/sponsors">
            <Button className="bg-white text-kerala-terracotta hover:bg-kerala-cream rounded-full px-8 py-4 font-medium">
              Become a Sponsor
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
